import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';

const getInitials = (user) => {
  if (!user) return 'U';
  if (user.first_name && user.last_name) {
    return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
  }
  if (user.email) {
    return user.email.charAt(0).toUpperCase();
  }
  return 'U';
};

const getRoleLabel = (role) => {
  const value = parseInt(role);
  switch (value) {
    case 0: return 'Super Admin';
    case 1: return 'User';
    case 2: return 'Organization Admin';
    default: return 'User';
  }
};

const ProfilePage = () => {
  const { user, getUserProfile, updateProfile } = useAuth();
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarRemove, setAvatarRemove] = useState(false);
  const [loading, setLoading] = useState(false);

  // Simple local form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [bio, setBio] = useState('');
  const [orgName, setOrgName] = useState('');

  const [errors, setErrors] = useState({});

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
  const uploadsBaseUrl = apiBaseUrl.replace(/\/api\/?$/, '');

  useEffect(() => {
    const init = async () => {
      try {
        const profileUser = await getUserProfile();
        if (profileUser) {
          setFirstName(profileUser.first_name || '');
          setLastName(profileUser.last_name || '');
          setEmail(profileUser.email || '');
          setPhone(profileUser.phone || '');
          setBio(profileUser.bio || '');
          setOrgName(profileUser.organization?.name || '');

          if (profileUser.avatar_path) {
            setAvatarPreview(`${uploadsBaseUrl}/${profileUser.avatar_path}`);
          }
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      }
    };

    init();
  }, []);

  const roleLabel = useMemo(() => (user ? getRoleLabel(user.role) : 'User'), [user]);

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPG, PNG, and WebP images are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5MB');
      return;
    }

    setAvatarFile(file);
    setAvatarRemove(false);

    const url = URL.createObjectURL(file);
    setAvatarPreview(url);
  };

  const handleRemoveAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
    setAvatarRemove(true);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!firstName.trim()) newErrors.first_name = 'First name is required';
    if (!lastName.trim()) newErrors.last_name = 'Last name is required';
    if (phone && phone.length > 20) newErrors.phone = 'Phone number is too long';
    if (bio && bio.length > 2000) newErrors.bio = 'Bio is too long';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    if (!validateForm()) {
      return;
    }

    if (!user) return;
    setLoading(true);

    const payload = {
      first_name: firstName,
      last_name: lastName,
      phone: phone,
      bio: bio,
      avatarFile,
      avatarRemove
    };

    if (parseInt(user.role) === 2) {
      payload.organization_name = orgName;
    }

    const result = await updateProfile(payload);
    setLoading(false);

    if (result.success) {
      setAvatarFile(null);
    }
  };

  const isOrgAdmin = user && parseInt(user.role) === 2;

  return (
    <div className="container-fluid px-0">
      <div className="row justify-content-center">
        <div className="col-12 col-lg-10 col-xl-8">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white border-0 pb-0">
              <h4 className="mb-1 fw-bold">My Profile</h4>
              <p className="text-muted mb-0">
                Manage your account information and profile picture.
              </p>
            </div>
            <div className="card-body">
              <form onSubmit={onSubmit} className="row g-4">
                <div className="col-12 d-flex flex-column flex-md-row align-items-center mb-3">
                  <div className="me-md-4 mb-3 mb-md-0 text-center">
                    <div className="position-relative d-inline-block">
                      {avatarPreview ? (
                        <img
                          src={avatarPreview}
                          alt="Avatar"
                          className="rounded-circle border"
                          style={{ width: 96, height: 96, objectFit: 'cover' }}
                        />
                      ) : (
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center bg-primary text-white fw-bold"
                          style={{ width: 96, height: 96, fontSize: '1.5rem' }}
                        >
                          {getInitials(user)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex-grow-1 text-center text-md-start">
                    <h5 className="mb-1">
                      {user?.first_name || user?.last_name
                        ? `${user?.first_name || ''} ${user?.last_name || ''}`.trim()
                        : email}
                    </h5>
                    <span className="badge bg-secondary me-2">{roleLabel}</span>
                    <div className="mt-3 d-flex flex-column flex-sm-row gap-2 justify-content-center justify-content-md-start">
                      <label className="btn btn-outline-primary mb-0">
                        Change Picture
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="d-none"
                          onChange={handleAvatarChange}
                        />
                      </label>
                      {avatarPreview && (
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={handleRemoveAvatar}
                        >
                          Remove Picture
                        </button>
                      )}
                    </div>
                    <small className="text-muted d-block mt-2">
                      JPG, PNG or WebP. Max 5MB.
                    </small>
                  </div>
                </div>

                <hr className="my-2" />

                <div className="col-12 col-md-6">
                  <label className="form-label fw-semibold">First Name</label>
                  <input
                    type="text"
                    className={`form-control ${errors.first_name ? 'is-invalid' : ''}`}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  {errors.first_name && (
                    <div className="invalid-feedback">{errors.first_name}</div>
                  )}
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label fw-semibold">Last Name</label>
                  <input
                    type="text"
                    className={`form-control ${errors.last_name ? 'is-invalid' : ''}`}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                  {errors.last_name && (
                    <div className="invalid-feedback">{errors.last_name}</div>
                  )}
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label fw-semibold">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={email}
                    disabled
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <small className="text-muted">Email cannot be changed.</small>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label fw-semibold">Phone</label>
                  <input
                    type="tel"
                    className="form-control"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                  {errors.phone && (
                    <div className="text-danger small">{errors.phone}</div>
                  )}
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold">Bio / About</label>
                  <textarea
                    rows={4}
                    className="form-control"
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    placeholder="Tell others a bit about yourself..."
                  />
                  {errors.bio && (
                    <div className="text-danger small">{errors.bio}</div>
                  )}
                </div>

                {isOrgAdmin && (
                  <div className="col-12">
                    <label className="form-label fw-semibold">Organization Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                    />
                    <small className="text-muted">
                      This is the name shown to learners for your organization.
                    </small>
                  </div>
                )}

                <div className="col-12 d-flex justify-content-end mt-3">
                  <button type="submit" className="btn btn-primary px-4" disabled={loading}>
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
